/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Central Transaction Clearing Journal (B2C)
 * PATH: src/routes/payout/TransactionPayoutList.js
 * COMPLIANCE: DPDP Act 2023 (Data Masking), NPCI BBPS Settlement Standards
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Space,
  Row,
  Col,
  Card,
  Table,
  DatePicker,
  Modal,
  Select,
  Tag,
  Tooltip,
  Typography,
  Input,
  Drawer,
  Descriptions,
  message,
} from "antd";
import dayjs from "dayjs";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { formatCurrency } from "utils/common";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CopyOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
  EyeInvisibleOutlined,
  SafetyCertificateOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import api from "utils/api";
import { payoutgetPartnerSummary } from "requests/statistic";
import { exportOrdersPayout } from "requests/order";
import { format } from "date-fns";
import "./TransactionPayoutList.css";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

function TransactionPayoutList() {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    Number(process.env.REACT_APP_RECORDS_PER_PAGE) || 10
  );
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Audit Detail Drawer State
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  // Status Change Modal State
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [targetStatus, setTargetStatus] = useState("");

  // DPDP Masking Visibility State
  const [revealedEmails, setRevealedEmails] = useState({});

  const location = useLocation();
  const navigate = useNavigate();

  // Synchronize with URL Query Parameters
  const getData = useCallback(async (query) => {
    try {
      setIsTableLoading(true);
      const activeQuery = query || parseQueryParams(location);
      const response = await payoutgetPartnerSummary(activeQuery);

      if (response && response.data) {
        setRecords(response.data);
        setPage(Number(response.current_page) || 1);
        setPerPage(Number(response.per_page) || 10);
        setTotalCount(Number(response.total) || 0);
      } else {
        setRecords([]);
        setTotalCount(0);
      }
    } catch (err) {
      message.error(err?.message || "Failed to load transaction ledger.");
    } finally {
      setIsTableLoading(false);
    }
  }, [location]);

  useEffect(() => {
    const query = parseQueryParams(location);
    if (query.payment_status) setStatusFilter(query.payment_status);
    if (query.keyword) setSearchTerm(query.keyword);
    getData(query);
  }, [location, getData]);

  // Currency Formatter
  const renderRupee = (val) => {
    const num = Number(val) || 0;
    try {
      if (typeof formatCurrency === "function") {
        return formatCurrency(num);
      }
    } catch (e) {
      // fallback
    }
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // DPDP Masking Helper
  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "N/A";
    const [name, domain] = email.split("@");
    if (name.length <= 2) return `${name}•••@${domain}`;
    return `${name.slice(0, 2)}${"•".repeat(Math.min(name.length - 2, 6))}@${domain}`;
  };
// Helper: Mask mobile keeping first 2 and last 2 digits visible
const maskPhone = (phone) => {
  if (!phone) return "";
  const clean = String(phone).replace(/\D/g, "");
  if (clean.length >= 10) {
    const last10 = clean.slice(-10);
    return `${last10.slice(0, 2)}******${last10.slice(-2)}`;
  }
  return phone;
};
  // Clipboard Copy Action
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  // Search Handler
  const handleSearch = (value) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: 1,
      keyword: value.trim(),
    };
    if (!value.trim()) delete query.keyword;

    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Status Filter Handler
  const handleStatusChange = (val) => {
    setStatusFilter(val);
    let query = parseQueryParams(location);
    if (val) {
      query = { ...query, page: 1, payment_status: val };
    } else {
      delete query.payment_status;
    }
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Date Range Filter Handler
  const handleDateChange = (dates) => {
    let query = parseQueryParams(location);
    if (dates && dates.length === 2) {
      query = {
        ...query,
        page: 1,
        created_at_date_min: dayjs(dates[0]).format("YYYY-MM-DD"),
        created_at_date_max: dayjs(dates[1]).format("YYYY-MM-DD"),
      };
    } else {
      delete query.created_at_date_min;
      delete query.created_at_date_max;
    }
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Table Pagination & Sorting
  const onChangeTable = (pagination) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: pagination.current,
      per_page: pagination.pageSize,
    };
    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  // Export Clearing Report
  const onExport = async () => {
    try {
      setIsTableLoading(true);
      const query = parseQueryParams(location);
      const response = await exportOrdersPayout(query);
      if (response && response.filepath) {
        window.open(
          `${process.env.REACT_APP_ASSET_URL || ""}${response.filepath}`,
          "_blank"
        );
        message.success("Clearing ledger export initiated.");
      } else {
        message.warning("No file generated for current filter range.");
      }
    } catch (err) {
      message.error("Failed to export transaction records.");
    } finally {
      setIsTableLoading(false);
    }
  };

  // Status Update Dialog Execution
  const handleStatusOk = async () => {
    if (!currentId || !targetStatus) return;

    try {
      setIsTableLoading(true);
      const formData = new FormData();
      formData.append("id", currentId);
      formData.append("status", targetStatus);

      await api.post(`/admin/transaction-update-status`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success("Transaction state updated successfully.");
      setIsStatusModalVisible(false);
      getData();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to update transaction status.");
    } finally {
      setIsTableLoading(false);
    }
  };

  // Status Badge Formatter
  const renderStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "success":
        return (
          <Tag icon={<CheckCircleFilled />} color="success" className="enterprise-status-tag">
            SUCCESS
          </Tag>
        );
      case "inprocess":
      case "pending":
        return (
          <Tag icon={<ClockCircleFilled />} color="warning" className="enterprise-status-tag">
            IN-FLIGHT
          </Tag>
        );
      case "failed":
        return (
          <Tag icon={<CloseCircleFilled />} color="error" className="enterprise-status-tag">
            FAILED
          </Tag>
        );
      default:
        return <Tag color="default">{status?.toUpperCase() || "UNKNOWN"}</Tag>;
    }
  };

  // Helper: Detect service type channel badge
  const getChannelBadge = (record) => {
    const isPrepaidCard =
      record.service_type === "prepaid_card" ||
      record.channel_badge === "Prepaid Cards" ||
      (record.operator_name && record.operator_name.toLowerCase().includes("prepaid card"));

    if (isPrepaidCard) {
      return <Tag color="cyan" className="service-category-tag">Prepaid Cards</Tag>;
    }

    if (
      record.service_type === "recharge" ||
      (record.operator_name && ["airtel", "jio", "vi", "bsnl"].includes(record.operator_name.toLowerCase()))
    ) {
      return <Tag color="geekblue" className="service-category-tag">Recharge</Tag>;
    }

    if (record?.bill_category?.name) {
      return <Tag color="blue" className="service-category-tag">{record.bill_category.name}</Tag>;
    }

    return <Tag color="purple" className="service-category-tag">{record.channel_badge || "Gift Voucher"}</Tag>;
  };

  const columns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => (
        <span className="sr-index">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      title: "Timestamp / User ID",
      width: 170,
      render: (_, record) => (
        <div className="meta-cell">
          <span className="txn-date">
            {record.date_time
              ? format(new Date(record.date_time), "dd MMM yyyy, hh:mm a")
              : "N/A"}
          </span>
          <span className="txn-user-id">UID: {record.user_id || "Direct B2C"}</span>
        </div>
      ),
    },
    {
      title: "Service Channel",
      width: 200,
      render: (_, record) => (
        <div className="service-cell">
          <span className="service-operator">{record.operator_name || "Direct Switch"}</span>
          {getChannelBadge(record)}
        </div>
      ),
    },
    {
  title: "Customer Details",
  width: 240,
  render: (_, record) => {
    const email = record?.user_detail?.email || record?.user_email || record?.customer_email || record?.email;
    const rawMobile = record?.user_detail?.mobile || record?.user_mobile || record?.customer_phone || record?.mobile;
    const cleanMobile = rawMobile ? String(rawMobile).replace(/\D/g, "").slice(-10) : "";
    const roleType = record?.user_detail?.role || record?.user_type || record?.role || "user";

    const recordKey = record.id || record._id;
    const isRevealed = revealedEmails[recordKey];

    return (
      <div className="customer-cell" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Email Row with Unmask Button */}
        <div className="customer-email-row" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span className="customer-email" style={{ fontSize: 13, fontWeight: 600, color: "#1e293b", wordBreak: "break-all" }}>
            {email ? (isRevealed ? email : maskEmail(email)) : "N/A"}
          </span>
          {email && (
            <Button
              type="text"
              size="small"
              className="unmask-btn"
              style={{ padding: "0 4px", height: "auto", color: "#64748b" }}
              icon={isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() =>
                setRevealedEmails((prev) => ({
                  ...prev,
                  [recordKey]: !prev[recordKey],
                }))
              }
            />
          )}
        </div>

        {/* Mobile Number Row */}
        {cleanMobile ? (
          <span className="customer-mobile" style={{ fontSize: 12, color: "#475569", fontFamily: "monospace" }}>
            +91 {isRevealed ? cleanMobile : maskPhone(cleanMobile)}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>Mobile Unlinked</span>
        )}

        {/* User Role */}
        <span className="customer-role" style={{ fontSize: 11, color: "#64748b" }}>
          Type: <strong style={{ color: "#0284c7", textTransform: "capitalize" }}>{roleType}</strong>
        </span>
      </div>
    );
  },
},
    {
      title: "Order & Clearing Ref",
      width: 230,
      render: (_, record) => {
        // Resolve primary ID gracefully so it never falls back to N/A when an ID exists
        const mainTxnId =
          record.txnRefId && record.txnRefId !== "N/A" && record.txnRefId !== "Pending"
            ? record.txnRefId
            : record.transaction_id && record.transaction_id !== "N/A" && record.transaction_id !== "Pending"
            ? record.transaction_id
            : record.gateway_txn_id && record.gateway_txn_id !== "N/A"
            ? record.gateway_txn_id
            : "Pending";

        const secondaryGatewayId =
          record.gateway_txn_id &&
          record.gateway_txn_id !== "N/A" &&
          record.gateway_txn_id !== mainTxnId
            ? record.gateway_txn_id
            : null;

        return (
          <div className="ref-cell">
            {/* Order Reference */}
            <div className="ref-item">
              <span className="ref-label">Order:</span>
              <span className="ref-value">{record.order_id || "N/A"}</span>
              {record.order_id && (
                <Tooltip title="Copy Order ID">
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => copyToClipboard(record.order_id, "Order ID")}
                  />
                </Tooltip>
              )}
            </div>

            {/* Merchant Reference ID (e.g. 31450677107) */}
            <div className="ref-item">
              <span className="ref-label">Txn ID:</span>
              <span className="ref-value">{mainTxnId}</span>
              {mainTxnId && mainTxnId !== "Pending" && (
                <Tooltip title="Copy Txn ID">
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => copyToClipboard(mainTxnId, "Txn ID")}
                  />
                </Tooltip>
              )}
            </div>

            {/* TBX 17-digit Load Money ID (e.g. 12627320350013943) */}
            {record.gateway_txn_id && record.gateway_txn_id !== mainTxnId && (
              <div className="ref-item">
                <span className="ref-label">TBX Ref:</span>
                <span className="ref-value font-mono text-xs" style={{ color: "#0958d9" }}>
                  {record.gateway_txn_id}
                </span>
                <Tooltip title="Copy TBX Load ID">
                  <CopyOutlined
                    className="copy-icon"
                    onClick={() => copyToClipboard(record.gateway_txn_id, "TBX Load ID")}
                  />
                </Tooltip>
              </div>
            )}

            <div className="ref-mode-pill">
              Mode: <strong>{record.paymentMode || record.payment_mode || "UPI"}</strong>
            </div>
          </div>
        );
      },
    },
    {
      title: "Financial Ledger",
      width: 220,
      render: (_, record) => {
        const gross = record.amount ?? 0;
        const fee = record.fee_amount ?? record.CustConvFee ?? record.fee ?? 0;
        const disc = record.discount ?? 0;
        const net = record.final_amount ?? record.set_amount ?? (Number(gross) + Number(fee) - Number(disc));

        return (
          <div className="financial-ledger-cell">
            <div className="fin-row total-row">
              <span>Gross:</span>
              <strong>{renderRupee(gross)}</strong>
            </div>
            <div className="fin-row net-row">
              <span>Net Cleared:</span>
              <span className="net-amount">{renderRupee(net)}</span>
            </div>
            <div className="fin-sub-info">
              <span>Fee: {renderRupee(fee)}</span>
              <span>Disc: {renderRupee(disc)}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      width: 130,
      align: "center",
      render: (_, record) => renderStatusBadge(record.status),
    },
    {
      title: "Actions",
      width: 100,
      align: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Inspect Transaction Audit Receipt">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedTxn(record);
                setIsDrawerVisible(true);
              }}
              className="audit-inspect-btn"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="transaction-ledger-container">
      {/* Top Header Card & Filter Matrix */}
      <Card className="ledger-header-card" bordered={false}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <div className="ledger-title-wrap">
              <ThunderboltFilled className="header-lightning-icon" />
              <div>
                <Title level={4} className="ledger-main-heading">
                  Financial Settlement Journal
                </Title>
                <Text type="secondary" className="ledger-subtext">
                  Real-time clearing logs, gateway references &amp; consumer settlements
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={16} className="ledger-controls-wrap">
            <Space wrap size="middle" className="controls-flex">
              {/* Payment Status Dropdown */}
              <Select
                placeholder="All Switch Statuses"
                value={statusFilter || undefined}
                onChange={handleStatusChange}
                allowClear
                className="status-filter-select"
              >
                <Option value="success">Success Only</Option>
                <Option value="pending">In-Flight / Pending</Option>
                <Option value="failed">Failed / Reversed</Option>
              </Select>

              {/* Universal Search Input */}
              <Input
  placeholder="Search Email, Mobile, Order ID, Txn ID, Channel..."
  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
  defaultValue={searchTerm}
  onPressEnter={(e) => handleSearch(e.target.value)}
  allowClear
  className="ledger-search-input"
  style={{ width: 340, borderRadius: 8 }}
/>

              {/* Date Range Picker */}
              <RangePicker
                onChange={handleDateChange}
                className="ledger-range-picker"
              />

              {/* Export Button */}
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={onExport}
                className="ledger-export-btn"
              >
                Export
              </Button>

              {/* Refresh Trigger */}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => getData()}
                className="ledger-refresh-btn"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Primary Financial Ledger Table */}
      <Card className="ledger-table-card" bordered={false}>
        <Table
          loading={isTableLoading}
          dataSource={records}
          columns={columns}
          onChange={onChangeTable}
          rowKey={(record) => record.id || record._id}
          pagination={{
            pageSize: perPage,
            total: totalCount,
            current: page,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} clearing entries`,
          }}
          scroll={{ x: 1200 }}
          className="enterprise-clearing-table"
        />
      </Card>

      {/* Transaction Audit Receipt Drawer */}
      <Drawer
        title="Transaction Audit Receipt"
        placement="right"
        width={500}
        onClose={() => setIsDrawerVisible(false)}
        visible={isDrawerVisible}
        className="txn-audit-drawer"
      >
        {selectedTxn && (
          <div className="audit-drawer-content">
            <div className="drawer-status-banner">
              <div>
                <span className="drawer-banner-title">Clearing Status</span>
                <div style={{ marginTop: 4 }}>{renderStatusBadge(selectedTxn.status)}</div>
              </div>
              <div className="drawer-gross-amount">
                <span className="gross-label">Settled Amount</span>
                <span className="gross-val">
                  {renderRupee(
                    selectedTxn.final_amount ??
                      selectedTxn.set_amount ??
                      selectedTxn.amount
                  )}
                </span>
              </div>
            </div>

            <Descriptions title="Order Identification" column={1} bordered size="small" className="mt-16">
              <Descriptions.Item label="Order Reference">
                <Text copyable>{selectedTxn.order_id || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Txn Ref ID">
                <Text copyable>{selectedTxn.txnRefId || selectedTxn.transaction_id || "N/A"}</Text>
              </Descriptions.Item>
              {selectedTxn.gateway_txn_id && (
                <Descriptions.Item label="TBX 17-digit Load ID">
                  <Text copyable style={{ color: "#0958d9", fontWeight: 600 }}>
                    {selectedTxn.gateway_txn_id}
                  </Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Payment Rail Mode">
                <Tag color="cyan">
                  {selectedTxn.paymentMode || selectedTxn.payment_mode || "UPI"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Creation Timestamp">
                {selectedTxn.date_time
                  ? format(new Date(selectedTxn.date_time), "dd-MM-yyyy hh:mm:ss a")
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Consumer & Biller Info" column={1} bordered size="small" className="mt-16">
              <Descriptions.Item label="Consumer Email">
                {selectedTxn?.user_detail?.email ||
                  selectedTxn?.user_email ||
                  selectedTxn?.email ||
                  "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Operator / Biller">
                {selectedTxn.operator_name || selectedTxn.biller_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Service Vertical">
                {selectedTxn.service_type === "prepaid_card"
                  ? "Prepaid Cards"
                  : selectedTxn?.bill_category?.name ?? "Gift Voucher"}
              </Descriptions.Item>
              {selectedTxn.failed_reason && (
                <Descriptions.Item label="Decline Reason">
                  <Text type="danger">{selectedTxn.failed_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Descriptions title="Accounting Breakdown" column={1} bordered size="small" className="mt-16">
              <Descriptions.Item label="Gross Inflow">
                {renderRupee(selectedTxn.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Platform Fee">
                {renderRupee(selectedTxn.fee_amount ?? selectedTxn.CustConvFee ?? selectedTxn.fee)}
              </Descriptions.Item>
              <Descriptions.Item label="Discount Subsidized">
                {renderRupee(selectedTxn.discount)}
              </Descriptions.Item>
              <Descriptions.Item label="GST (18%)">
                {renderRupee(selectedTxn.gst)}
              </Descriptions.Item>
              <Descriptions.Item label="TDS">
                {renderRupee(selectedTxn.tds)}
              </Descriptions.Item>
              <Descriptions.Item label="Net Cleared Balance">
                <strong>
                  {renderRupee(
                    selectedTxn.final_amount ??
                      selectedTxn.set_amount ??
                      (Number(selectedTxn.amount || 0) +
                        Number(selectedTxn.fee_amount ?? selectedTxn.CustConvFee ?? selectedTxn.fee ?? 0) -
                        Number(selectedTxn.discount || 0))
                  )}
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>

      {/* Manual Status Modification Modal */}
      <Modal
        title="Admin Clearing Override"
        visible={isStatusModalVisible}
        onOk={handleStatusOk}
        onCancel={() => setIsStatusModalVisible(false)}
        okText="Confirm State Override"
      >
        <Text type="secondary" className="mb-12 block">
          Manually altering transaction state bypasses automated webhook reconciliation.
        </Text>
        <Select
          placeholder="Select Target State"
          onChange={(val) => setTargetStatus(val)}
          value={targetStatus}
          style={{ width: "100%", marginTop: 8 }}
        >
          <Option value="success">Success (Force Cleared)</Option>
          <Option value="failed">Failed (Trigger Auto-Reversal)</Option>
        </Select>
      </Modal>
    </div>
  );
}

export default TransactionPayoutList;